import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';

const API_BASE = '/api';

const readError = (e, fallback) =>
  e?.response?.data?.message || e?.message || fallback;

const fmt = (value, suffix = '') =>
  value === null || value === undefined || Number.isNaN(Number(value))
    ? '-'
    : `${Number(value).toFixed(1)}${suffix}`;

const timeLabel = (epoch) => {
  if (!epoch) return '-';
  return new Date(epoch * 1000).toLocaleTimeString('ko-KR', {
    hour: '2-digit',
    minute: '2-digit',
  });
};

function MiniLine({ points = [], suffix = '' }) {
  const values = points.map((p) => Number(p.value)).filter(Number.isFinite);
  if (values.length < 2) {
    return <div className="text-secondary small py-4 text-center">표시할 측정값이 없습니다.</div>;
  }
  const min = Math.min(...values);
  const max = Math.max(...values);
  const span = max - min || 1;
  const width = 800;
  const height = 180;
  const left = 90;
  const right = 10;
  const top = 12;
  const bottom = 12;
  const plotWidth = width - left - right;
  const plotHeight = height - top - bottom;
  const ticks = [max, max - span / 3, max - (span * 2) / 3, min];

  const poly = points
    .map((p, i) => {
      const x = left + (i / Math.max(points.length - 1, 1)) * plotWidth;
      const y = top + ((max - Number(p.value)) / span) * plotHeight;
      return `${x},${y}`;
    })
    .join(' ');

  return (
    <>
      <svg viewBox={`0 0 ${width} ${height}`} className="w-100" style={{ height: 180 }}>
        {ticks.map((tick, i) => {
          const y = top + (i / (ticks.length - 1)) * plotHeight;
          return (
            <g key={i}>
              <line
                x1={left}
                y1={y}
                x2={width - right}
                y2={y}
                stroke="currentColor"
                opacity=".12"
              />
              <text
                x={left - 12}
                y={y}
                textAnchor="end"
                dominantBaseline="middle"
                fill="currentColor"
                opacity=".65"
                fontSize="18"
              >
                {fmt(tick, suffix)}
              </text>
            </g>
          );
        })}
        <polyline
          points={poly}
          fill="none"
          stroke="currentColor"
          strokeWidth="4"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>
      <div className="d-flex justify-content-between small text-secondary">
        <span>{timeLabel(points[0]?.time)}</span>
        <span>최저 {fmt(min, suffix)} · 최고 {fmt(max, suffix)}</span>
        <span>{timeLabel(points[points.length - 1]?.time)}</span>
      </div>
    </>
  );
}

function MetricCard({ title, value, sub, children }) {
  return (
    <div className="col-6 col-lg-3">
      <div className="card border-0 shadow-sm h-100">
        <div className="card-body">
          <div className="text-secondary small mb-2">{title}</div>
          <div className="fs-3 fw-bold">{value}</div>
          {sub ? <div className="small text-secondary mt-1">{sub}</div> : null}
          {children}
        </div>
      </div>
    </div>
  );
}

export default function TodayFarmPage() {
  const [farms, setFarms] = useState([]);
  const [farmId, setFarmId] = useState('');
  const [period, setPeriod] = useState('today');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    axios.get(`${API_BASE}/farms`)
      .then((r) => {
        const list = r.data?.data || [];
        setFarms(list);
        if (list.length) {
          setFarmId(String(list[0].id));
        } else {
          setLoading(false);
        }
      })
      .catch((e) => {
        setError(readError(e, '농장 목록을 불러오지 못했습니다.'));
        setLoading(false);
      });
  }, []);

  const load = useCallback(async () => {
    if (!farmId) return;
    setLoading(true);
    setError('');
    try {
      const r = await axios.get(`${API_BASE}/farms/${farmId}/today-farm`, { params: { period } });
      setData(r.data?.data || null);
    } catch (e) {
      setError(readError(e, '오늘 농장 데이터를 불러오지 못했습니다.'));
    } finally {
      setLoading(false);
    }
  }, [farmId, period]);

  useEffect(() => { load(); }, [load]);

  const s = data?.summary || {};
  const series = data?.series || {};
  const irrigationReady = s.irrigation_count !== null && s.irrigation_count !== undefined;

  return (
    <div className="container-fluid py-4" style={{ width: '100%', maxWidth: '1100px' }}>
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-3 mb-4">
        <div>
          <h3 className="fw-bold mb-2">오늘 농장</h3>
          <div className="text-secondary mb-2" style={{ fontSize: '0.9rem' }}>데이터를 농부가 보기 쉽게 요약합니다.</div>
        </div>
        <div className="d-flex flex-wrap gap-2 w-100">
          <select className="form-select w-100" value={farmId} onChange={(e) => setFarmId(e.target.value)}>
            {farms.map((f) => <option key={f.id} value={f.id}>{f.name}</option>)}
          </select>
        </div>
      </div>

      <div className="d-flex mb-4">
        <div className="btn-group w-100" role="group">
          {[['today','오늘'],['yesterday','어제'],['7d','7일'],['30d','30일']].map(([v,l]) => (
            <button key={v} type="button" className={`btn ${period === v ? 'btn-primary' : 'btn-outline-primary'}`} style={{ fontSize: '0.8rem' }} onClick={() => setPeriod(v)}>{l}</button>
          ))}
          <button type="button" className="btn btn-outline-primary" style={{ fontSize: '0.8rem' }} onClick={load}>새로고침</button>
        </div>
      </div>

      {error ? <div className="alert alert-danger">{error}</div> : null}
      {loading ? <div className="py-5 text-center"><div className="spinner-border text-primary" /></div> : null}

      {!loading && data ? <>
        <div className="row g-3 mb-4">
          <MetricCard title="현재 토양수분" value={fmt(s.soil_moisture?.current, '%')} sub={`최저 ${fmt(s.soil_moisture?.min, '%')} · 최고 ${fmt(s.soil_moisture?.max, '%')}`} />
          <MetricCard title="현재 기온" value={fmt(s.temperature?.current, '℃')} sub={`최저 ${fmt(s.temperature?.min, '℃')} · 최고 ${fmt(s.temperature?.max, '℃')}`} />
          <MetricCard title="현재 습도" value={fmt(s.humidity?.current, '%')} sub={`평균 ${fmt(s.humidity?.avg, '%')}`} />
          <MetricCard title="강우" value={fmt(s.rainfall?.current, ' mm')} sub={`${data.period_label} Mycodo 측정값`} />
        </div>

        <div className="card border-0 shadow-sm mb-4">
          <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center">
            <strong>관수</strong>
            <span className="badge text-bg-light">Mycodo</span>
          </div>
          <div className="card-body">
            {irrigationReady ? (
              <div className="row g-3">
                <div className="col-4"><div className="text-secondary small">횟수</div><div className="fs-4 fw-bold">{s.irrigation_count}회</div></div>
                <div className="col-4"><div className="text-secondary small">총 가동</div><div className="fs-4 fw-bold">{Math.round((s.irrigation_total_seconds || 0) / 60)}분</div></div>
                <div className="col-4"><div className="text-secondary small">마지막 관수</div><div className="fs-4 fw-bold">{timeLabel(s.last_irrigation_at)}</div></div>
              </div>
            ) : (
              <div>
                <div className="fw-semibold mb-1">관수 이력 연결 대기</div>
                <div className="text-secondary small">
                  현재 상태는 Mycodo Output API에서 읽되, 관수 횟수와 총 가동시간은 검증되지 않은 값으로 추측하지 않습니다.
                  펌프의 실제 Mycodo 이력 소스가 확인되면 이 카드에 바로 계산됩니다.
                </div>
              </div>
            )}
          </div>
        </div>

        <div className="row g-4">
          <div className="col-12 col-xl-7">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white py-3"><strong>{data.period_label} 토양수분 변화</strong></div>
              <div className="card-body"><MiniLine points={series.soil_moisture || []} suffix="%" /></div>
            </div>
          </div>
          <div className="col-12 col-xl-5">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-header bg-white py-3"><strong>{data.period_label} 기온 변화</strong></div>
              <div className="card-body"><MiniLine points={series.temperature || []} suffix="℃" /></div>
            </div>
          </div>
        </div>

        <div className="text-secondary small mt-3 mx-3">
          연결: {data.site_name || '-'} · Input {data.mycodo?.inputs ?? 0} · Output {data.mycodo?.outputs ?? 0} · Function {data.mycodo?.functions ?? 0}
        </div>
      </> : null}
    </div>
  );
}
